/**
 * MosaicParentView.js — v6.32.168
 *
 * Public, no-auth, read-only parent/guardian growth view. Resolves a
 * magic-link token via GET /api/mosaic/parent-view/{token} and renders
 * the same growth-card + action-panel + MOSAIC content engine teacher-note that
 * teachers see inside the MosaicIntakeModal — but stripped of any
 * staff-only chrome.
 */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Sparkles } from 'lucide-react';
import MosaicActionPanel from '@/components/mosaic/MosaicActionPanel';
import MosaicGrowthCard from '@/components/mosaic/MosaicGrowthCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MosaicParentView = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) return;
    setLoading(true); setError(null);
    axios.get(`${API}/mosaic/parent-view/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => {
        const code = e?.response?.status;
        if (code === 410) setError('This growth link has expired. Please contact your school for a new one.');
        else if (code === 404) setError('This growth link is invalid. Please contact your school for a new one.');
        else setError(e?.response?.data?.detail || 'Failed to load growth update.');
      })
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-testid="parent-view-loading">
      <Loader2 className="w-6 h-6 animate-spin text-violet-600 mr-2" />
      <span className="text-gray-600">Loading growth update…</span>
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4" data-testid="parent-view-error">
      <div className="max-w-md border-2 border-rose-300 bg-white p-6">
        <div className="text-[11px] uppercase tracking-widest text-rose-700 font-bold mb-2">Link Issue</div>
        <p className="text-sm text-gray-800">{error}</p>
      </div>
    </div>
  );

  if (!data) return null;

  const studentName = data.student?.name || 'Your student';
  const studentGrade = data.student?.grade;
  const ui = data.translated_ui || {};
  const tp = data.translated_payload || {};
  const locale = (data.locale || 'en').toLowerCase();
  const isRTL = locale === 'ar';

  // Decorate the action panel cells with translations if present.
  const localizedPanel = (() => {
    if (!data.panel) return null;
    const td = (tp.panel && tp.panel.domains) || {};
    return {
      ...data.panel,
      composite_meta: {
        ...data.panel.composite_meta,
        label:   tp?.panel?.composite?.label   || data.panel.composite_meta?.label,
        summary: tp?.panel?.composite?.summary || data.panel.composite_meta?.summary,
      },
      domains: (data.panel.domains || []).map((d) => {
        const t = td[d.code] || {};
        return {
          ...d,
          label:     t.label || d.label,
          can_do:    (t.can_do    && t.can_do.filter(Boolean).length === (d.can_do    || []).length) ? t.can_do    : d.can_do,
          ready_for: (t.ready_for && t.ready_for.filter(Boolean).length === (d.ready_for || []).length) ? t.ready_for : d.ready_for,
          scaffolds: (t.scaffolds && t.scaffolds.filter(Boolean).length === (d.scaffolds || []).length) ? t.scaffolds : d.scaffolds,
        };
      }),
    };
  })();

  const note = tp.teacher_note || data.latest_teacher_note;
  const firstName = studentName.split(' ')[0];
  const panelHeading = (ui.panel_heading_template || "What {first} Can Do · What's Next").replace('{first}', firstName);

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4" data-testid="parent-view" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <header className="mb-8 border-2 border-gray-900 bg-white">
          <div className="px-5 py-4 bg-gray-900 text-white">
            <div className="text-[11px] uppercase tracking-widest text-violet-200 font-bold mb-1 flex items-center gap-2">
              <Sparkles className="w-3.5 h-3.5" /> {ui.eyebrow || 'MOSAIC Growth Update'}
            </div>
            <h1 className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {studentName}
            </h1>
            {studentGrade ? (
              <div className="text-sm text-gray-300 mt-0.5">
                {ui.level || 'Grade'} {studentGrade}
              </div>
            ) : null}
            <div className="text-[10px] uppercase tracking-widest text-violet-200 mt-1" data-testid="parent-view-locale">
              {locale.toUpperCase()}
            </div>
          </div>
          <div className="px-5 py-3 text-sm text-gray-700 bg-violet-50/40 border-t border-violet-200">
            {ui.subbanner || "This is a read-only growth update from your student's MOSAIC English Language Learner program. No sign-in is required. Your school will send a fresh update each quarter."}
          </div>
        </header>

        {/* Latest MOSAIC engine teacher note */}
        {note ? (
          <section className="mb-6" data-testid="parent-view-note">
            <h2 className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-2">
              {ui.note_heading || "Teacher's Most-Recent Note"}
            </h2>
            <blockquote className="border-l-4 border-violet-500 bg-white p-4 italic text-gray-800 text-base leading-snug">
              "{note}"
            </blockquote>
          </section>
        ) : null}

        {/* Growth card */}
        {data.growth ? (
          <section className="mb-6" data-testid="parent-view-growth">
            <h2 className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-2">
              {ui.growth_heading || 'Growth Over Time'}
            </h2>
            <ParentGrowthInline growth={data.growth} ui={ui} />
          </section>
        ) : null}

        {/* Action panel */}
        {localizedPanel ? (
          <section className="mb-6" data-testid="parent-view-panel">
            <h2 className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-2">
              {panelHeading}
            </h2>
            <MosaicActionPanel panel={localizedPanel} />
          </section>
        ) : (
          <div className="border border-gray-200 bg-white p-4 text-sm text-gray-500 italic mb-6">
            {ui.no_intake_note || 'Detailed growth information will appear here once the first MOSAIC intake is recorded.'}
          </div>
        )}

        {/* Footer */}
        <footer className="mt-10 pt-4 border-t border-gray-200 text-xs text-gray-500 text-center">
          {ui.footer || 'Sent by MOSAIC · Independent NYC ENL instructional infrastructure.'} ·
          {data.expires_at ? ` ${ui.expires_prefix || 'Link expires'} ${new Date(data.expires_at).toLocaleDateString()}` : ''}
        </footer>
      </div>
    </div>
  );
};

// Lightweight inline growth panel that reuses the parent payload (no second fetch).
const ParentGrowthInline = ({ growth, ui = {} }) => {
  const { current, projections = [], weekly_growth, on_track, target_weekly_growth, history = [] } = growth;
  if (!current) return null;
  return (
    <div className="border-2 border-violet-300 bg-violet-50/30">
      <div className="px-3 py-2 bg-violet-700 text-white flex items-center justify-between">
        <div className="text-[11px] font-bold uppercase tracking-widest">{ui.current_level_label || 'Current MOSAIC Level'}</div>
        <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 border ${on_track ? 'border-emerald-300 text-emerald-100 bg-emerald-800/30' : 'border-amber-300 text-amber-100 bg-amber-800/30'}`}>
          {on_track ? (ui.on_track || 'On Track') : (ui.building_pace || 'Building Pace')}
        </span>
      </div>
      <div className="p-4 space-y-3">
        <div className="flex items-baseline gap-3">
          <span className="text-3xl font-bold tabular-nums" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {current.composite}
          </span>
          <span className="text-sm text-gray-700">{ui.level || 'Level'} {current.level} {ui.of_5 || 'of 5'}</span>
        </div>
        {history.length >= 2 && (
          <div className="text-sm text-gray-700">
            {ui.weekly_growth_label || 'Weekly growth rate:'} <strong>{weekly_growth >= 0 ? '+' : ''}{weekly_growth.toFixed(3)}</strong>
            <span className="text-gray-500"> ({ui.target_label || 'target'} {target_weekly_growth})</span>
          </div>
        )}
        {projections.length > 0 && (
          <div className="grid grid-cols-3 gap-2 pt-1">
            {projections.map((p) => (
              <div key={p.weeks_ahead} className="border border-violet-200 bg-white p-2">
                <div className="text-[10px] uppercase tracking-widest text-gray-500">
                  {(ui.in_n_weeks || 'In {n} wks').replace('{n}', p.weeks_ahead)}
                </div>
                <div className="text-base font-bold tabular-nums">{p.projected_composite}</div>
                <div className="text-xs text-gray-500">{ui.level || 'Level'} {p.projected_level}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MosaicParentView;
